import { FIELDS, LABEL_TEMPLATES } from "../lib/fields";

import styles from "./MapColumnsStep.module.css";

import { ChevronLeftIcon } from "@/components/icons";

interface MapColumnsStepProps {
  headers: string[];
  mapping: Record<string, string>;
  labelTemplate: string;
  hasDupes: boolean;
  canGenerate: boolean;
  isWorking: boolean;
  statusMessage: string | null;
  statusKind: "error" | "success" | null;
  onBack: () => void;
  onChangeMapping: (key: string, value: string) => void;
  onChangeLabelTemplate: (value: string) => void;
  onGenerate: () => void;
}

export function MapColumnsStep({
  headers,
  mapping,
  labelTemplate,
  hasDupes,
  canGenerate,
  isWorking,
  statusMessage,
  statusKind,
  onBack,
  onChangeMapping,
  onChangeLabelTemplate,
  onGenerate,
}: MapColumnsStepProps) {
  return (
    <section className={styles.step} aria-labelledby="map-heading">
      <div className={styles.headRow}>
        <button type="button" className={styles.back} onClick={onBack} aria-label="Back to upload">
          <ChevronLeftIcon aria-hidden="true" />
        </button>
        <h2 id="map-heading" className={styles.heading}>
          Map columns
        </h2>
      </div>
      <p className={styles.intro}>Match your CSV columns to the letter fields, then generate.</p>

      <label className={styles.fieldLabel} htmlFor="label-template">
        Label template
      </label>
      <select
        id="label-template"
        className={styles.select}
        value={labelTemplate}
        onChange={(event) => onChangeLabelTemplate(event.target.value)}
      >
        {LABEL_TEMPLATES.map((option) => (
          <option key={option.key} value={option.key}>
            {option.label}
          </option>
        ))}
      </select>

      <table className={styles.table}>
        <thead>
          <tr>
            <th scope="col">Letter field</th>
            <th scope="col">Your CSV column</th>
          </tr>
        </thead>
        <tbody>
          {FIELDS.map((field) => {
            const value = mapping[field.key] ?? "";
            const matched = value !== "";
            const selectId = `map-${field.key}`;
            return (
              <tr key={field.key}>
                <td>
                  <label htmlFor={selectId} className={styles.rowLabel}>
                    {field.label}
                    {field.optional ? (
                      <span className={styles.optional}> (optional)</span>
                    ) : (
                      <span className={styles.requiredMark} aria-hidden="true">
                        {" "}
                        *
                      </span>
                    )}
                  </label>
                </td>
                <td>
                  <select
                    id={selectId}
                    className={matched ? `${styles.select} ${styles.matched}` : styles.select}
                    value={value}
                    onChange={(event) => onChangeMapping(field.key, event.target.value)}
                  >
                    <option value="">
                      {field.optional
                        ? field.key === "date"
                          ? "— Not mapped (uses today's date) —"
                          : "— Not mapped —"
                        : "— Select column —"}
                    </option>
                    {headers.map((header) => (
                      <option key={header} value={header}>
                        {header}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {hasDupes ? (
        <p className={styles.warning} role="alert">
          Two fields are mapped to the same column. Each field must use a unique column.
        </p>
      ) : null}

      <div aria-live="polite">
        {statusMessage ? (
          <p
            className={statusKind === "error" ? styles.error : styles.success}
            role={statusKind === "error" ? "alert" : undefined}
          >
            {statusMessage}
          </p>
        ) : null}
      </div>

      <button type="button" className={styles.primary} disabled={!canGenerate} onClick={onGenerate}>
        {isWorking ? "Generating…" : "Generate letters + labels"}
      </button>
    </section>
  );
}
